import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { generateCodeChallenge, generateCodeVerifier, randomString } from '../utils/pkce'
import { isEmbedded, isInExtension, sendExtensionMessage, sendPostMessage } from '../utils/environment'

// Define types
export interface SpotifyTokens {
  accessToken: string
  refreshToken: string
  expiresAt: number
}

interface SpotifyTrack {
  title: string
  artist: string
}

interface SpotifyPlaylist {
  id: string
  name: string
  images: { url: string }[]
  tracks: { total: number }
}

interface PlaylistOptions {
  trackCount: number
}

interface SpotifyTokenResponse {
  access_token: string
  token_type: string
  expires_in: number
  refresh_token: string
  scope: string
}

// Simple random state generator
const generateRandomState = () => {
  return Math.random().toString(36).substring(2, 15);
}

export function useSpotify(initialTokens: SpotifyTokens | null = null, setTokens?: (tokens: SpotifyTokens | null) => void) {
  // Internal state for tokens if not provided externally
  const [spotifyTokens, setSpotifyTokens] = useState<SpotifyTokens | null>(initialTokens);
  
  // Use the effective tokens (either from props or internal state)
  const tokens = initialTokens || spotifyTokens;
  
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [clientId, setClientId] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null)
  
  // Spotify API endpoints
  const SPOTIFY_API_URL = 'https://api.spotify.com/v1'
  const REDIRECT_URI = typeof window !== 'undefined' 
    ? `${window.location.origin}`
    : ''
  
  // Determine if we're in an embedded context
  const [isEmbeddedView] = useState(isEmbedded())
  const [isExtension] = useState(isInExtension())
  
  // Check if token is valid
  const isTokenValid = useCallback((): boolean => {
    if (!tokens) return false;
    // Check if token is expired (with 5 minute buffer)
    return tokens.expiresAt > Date.now() + 5 * 60 * 1000;
  }, [tokens])
  
  // Refresh token automatically
  useEffect(() => {
    if (!tokens) return
    
    // Clear any existing interval
    if (refreshInterval) {
      clearInterval(refreshInterval)
    }
    
    // Calculate time until expiry minus a 5-minute buffer
    const timeUntilExpiry = tokens.expiresAt - Date.now() - 5 * 60 * 1000
    
    // If token is expired or will expire in less than 5 minutes, refresh now
    if (timeUntilExpiry <= 0) {
      refreshAccessToken()
      return
    }
    
    // Set up interval to refresh token 5 minutes before it expires
    const interval = setInterval(() => {
      console.log('Refreshing access token...')
      refreshAccessToken()
    }, timeUntilExpiry)
    
    setRefreshInterval(interval)
    
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [tokens])
  
  // Automatically refresh token when browser tab becomes active again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && tokens && !isTokenValid()) {
        console.log('Tab became visible, refreshing token...')
        refreshAccessToken()
      }
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [tokens, isTokenValid])

  const refreshAccessToken = useCallback(async () => {
    if (!tokens?.refreshToken) {
      setError('No refresh token available')
      return
    }
    
    try {
      setIsLoading(true)
      setError(null)
      
      const response = await fetch('/api/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          grant_type: 'refresh_token',
          refresh_token: tokens.refreshToken,
          client_id: clientId
        })
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        console.error('Failed to refresh token:', errorData)
        throw new Error(errorData.error || 'Failed to refresh token')
      }
      
      const data: SpotifyTokenResponse = await response.json()
      
      const newTokens: SpotifyTokens = {
        accessToken: data.access_token,
        // If a new refresh token is provided, use it, otherwise keep the old one
        refreshToken: data.refresh_token || tokens.refreshToken,
        expiresAt: Date.now() + data.expires_in * 1000
      }
      
      setSpotifyTokens(newTokens)
      setError(null)
      console.log('Token refreshed successfully')
      
      return newTokens.accessToken
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to refresh access token'
      console.error('Error refreshing token:', errorMessage)
      setError(errorMessage)
      // Don't clear tokens on refresh error; we can try again later
      return null
    } finally {
      setIsLoading(false)
    }
  }, [tokens, clientId, setSpotifyTokens])

  // Fetch client ID from our Worker
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch('/api/config')
        if (!response.ok) throw new Error('Failed to load configuration')
        const config = await response.json()
        setClientId(config.clientId)
        setError(null)
      } catch (err) {
        console.error('Error loading configuration:', err)
        setError('Failed to load configuration')
      } finally {
        setIsLoading(false)
      }
    }
    
    fetchConfig()
  }, [])
  
  // Update authentication status when tokens change
  useEffect(() => {
    setIsAuthenticated(Boolean(tokens?.accessToken && tokens.expiresAt > Date.now()))
  }, [tokens])
  
  // Helper function to handle and save tokens
  const handleTokens = useCallback((newTokens: SpotifyTokens) => {
    if (newTokens && newTokens.accessToken) {
      // Save tokens to internal state
      setSpotifyTokens(newTokens);
      
      // Update external state if provided
      if (setTokens) {
        setTokens(newTokens);
      }
      
      // Also save to localStorage for persistence
      localStorage.setItem('spotify-tokens', JSON.stringify(newTokens));
      
      // Clear any existing errors
      setError(null);
      
      // Set authentication state
      setIsAuthenticated(true);
      
      console.log('Spotify tokens saved successfully');
    } else {
      console.error('Invalid tokens received');
      setError('Authentication failed: Invalid tokens');
    }
  }, [setTokens, setIsAuthenticated]);
  
  // Modified connectToSpotify function that handles both direct and proxy auth
  const connectToSpotify = useCallback(() => {
    // If in an extension/webview, use the proxy approach
    if (isEmbeddedView) {
      console.log('Using proxy authentication for Spotify in embedded view')
      
      // Set loading state
      setIsLoading(true)
      setError(null)
      
      // Try to use extension messaging if available
      if (isExtension) {
        sendExtensionMessage({
          type: 'spotify_auth_request',
          clientId
        })
          .then((response: any) => {
            if (response.tokens) {
              // Save the tokens we got from the extension
              handleTokens(response.tokens)
              console.log('Authentication completed via extension proxy')
            } else {
              throw new Error('No tokens received from extension')
            }
          })
          .catch(err => {
            console.error('Extension auth error:', err)
            setError(`Authentication failed: ${err.message}`)
            // Fall back to standard postMessage
            performPostMessageAuth()
          })
          .finally(() => {
            setIsLoading(false)
          })
      } else {
        // Use postMessage for non-extension embedded contexts
        performPostMessageAuth()
      }
    } else {
      // Standard flow for non-embedded contexts
      console.log('Using direct Spotify authentication')
      performDirectAuth()
    }
  }, [clientId, isEmbeddedView, isExtension])
  
  // Perform authentication via postMessage to parent
  const performPostMessageAuth = useCallback(() => {
    setIsLoading(true)
    setError(null)
    
    sendPostMessage({
      type: 'spotify_auth_request',
      clientId,
      scope: 'streaming user-read-email user-read-private user-library-read user-library-modify user-read-playback-state user-modify-playback-state playlist-modify-public playlist-modify-private'
    })
      .then((response: any) => {
        if (response.tokens) {
          handleTokens(response.tokens)
          console.log('Authentication completed via postMessage proxy')
        } else {
          throw new Error('No tokens received from parent')
        }
      })
      .catch(err => {
        console.error('PostMessage auth error:', err)
        setError(`Authentication failed: ${err.message}`)
        // As a last resort, try direct auth even in embedded view
        performDirectAuth()
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [clientId])
  
  // Standard direct authentication
  const performDirectAuth = useCallback(() => {
    // Use the root as the redirect URI
    const redirect_uri = window.location.origin 
    const state = randomString(16)
    const scope = 'streaming user-read-email user-read-private user-library-read user-library-modify user-read-playback-state user-modify-playback-state playlist-modify-public playlist-modify-private'

    // Store state in localStorage for validation
    localStorage.setItem('spotify_auth_state', state)
    
    // Create code verifier and challenge
    const codeVerifier = generateCodeVerifier()
    localStorage.setItem('code_verifier', codeVerifier)
    
    // Generate code challenge from verifier
    generateCodeChallenge(codeVerifier).then(codeChallenge => {
      console.log('Starting auth flow with PKCE')
      
      // Create authorization URL with PKCE parameters
      const authUrl = new URL('https://accounts.spotify.com/authorize')
      
      // Add query parameters
      const params = {
        response_type: 'code',
        client_id: clientId,
        scope,
        redirect_uri,
        state,
        code_challenge_method: 'S256',
        code_challenge: codeChallenge,
      }
      
      // Append all parameters to URL
      Object.keys(params).forEach(key => {
        authUrl.searchParams.append(key, params[key as keyof typeof params])
      })
      
      // In embedded view, use popup to avoid webview issues
      // In regular browser, use same tab
      if (isEmbeddedView) {
        // Open in a new tab for embedded views
        const authWindow = window.open(authUrl.toString(), '_blank')
        
        // Set up a message listener to detect when the auth is complete
        const messageListener = (event: MessageEvent) => {
          // Make sure the message is from our domain and contains auth info
          if (event.origin === window.location.origin && event.data?.type === 'spotify_auth_complete') {
            console.log('Received auth complete message')
            
            // Remove the event listener
            window.removeEventListener('message', messageListener)
            
            // Close the auth window
            if (authWindow && !authWindow.closed) {
              authWindow.close()
            }
            
            // Force a refresh of the current page to update auth state
            window.location.reload()
          }
        }
        
        // Add the event listener
        window.addEventListener('message', messageListener)
      } else {
        // For regular browsers, redirect in the same tab
        window.location.href = authUrl.toString()
      }
    })
  }, [clientId, isEmbeddedView])
  
  const handleSpotifyCallback = useCallback(async (code: string) => {
    try {
      setIsLoading(true)
      setError(null)
      
      // Get code verifier from session storage
      const codeVerifier = sessionStorage.getItem('spotify_code_verifier')
      if (!codeVerifier) {
        throw new Error('PKCE code verifier not found')
      }
      
      // Exchange code for tokens
      const response = await fetch('/api/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          grant_type: 'authorization_code',
          code,
          redirect_uri: REDIRECT_URI,
          code_verifier: codeVerifier,
          client_id: clientId
        })
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error_description || errorData.error || 'Failed to exchange code for tokens')
      }
      
      const data: SpotifyTokenResponse = await response.json()
      
      // Store tokens in state and localStorage
      const newTokens: SpotifyTokens = {
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresAt: Date.now() + data.expires_in * 1000
      }
      
      setSpotifyTokens(newTokens)
      
      // Clear session storage items
      sessionStorage.removeItem('spotify_code_verifier')
      sessionStorage.removeItem('spotify_auth_state')
      
      return data
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to handle callback'
      console.error('Callback error:', errorMessage)
      setError(errorMessage)
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [clientId, REDIRECT_URI, setSpotifyTokens])
  
  const logout = useCallback(() => {
    setSpotifyTokens(null)
    if (refreshInterval) clearInterval(refreshInterval)
    setRefreshInterval(null)
  }, [setSpotifyTokens, refreshInterval])

  // Modified API fetch to use proxy when in embedded context
  const spotifyFetch = useCallback(
    async (url: string, options: RequestInit = {}) => {
      if (!tokens?.accessToken) {
        throw new Error('Not authenticated')
      }
      
      try {
        // If we're in an embedded view, try to use the proxy
        if (isEmbeddedView) {
          let response
          
          // Try extension messaging first if available
          if (isExtension) {
            try {
              response = await sendExtensionMessage({
                type: 'spotify_api_request',
                url,
                options: {
                  ...options,
                  headers: {
                    ...options.headers,
                    Authorization: `Bearer ${tokens.accessToken}`,
                  },
                },
              })
              return response
            } catch (err) {
              console.error('Extension API proxy error:', err)
              // Fall back to postMessage
            }
          }
          
          // Try postMessage as fallback
          try {
            response = await sendPostMessage({
              type: 'spotify_api_request',
              url,
              options: {
                ...options,
                headers: {
                  ...options.headers,
                  Authorization: `Bearer ${tokens.accessToken}`,
                },
              },
            })
            return response
          } catch (err) {
            console.error('PostMessage API proxy error:', err)
            // Fall back to direct API call as last resort
          }
        }
        
        // Default direct API call
        const response = await fetch(url, {
          ...options,
          headers: {
            ...options.headers,
            Authorization: `Bearer ${tokens.accessToken}`,
          },
        })
        
        // Check if the token has expired
        if (response.status === 401) {
          // Try to refresh the token
          await refreshAccessToken()
          
          // Retry the request with the new token
          return fetch(url, {
            ...options,
            headers: {
              ...options.headers,
              Authorization: `Bearer ${tokens.accessToken}`,
            },
          }).then(res => res.json())
        }
        
        return response.json()
      } catch (error) {
        console.error('API request error:', error)
        throw error
      }
    },
    [tokens, refreshAccessToken, isEmbeddedView, isExtension]
  )

  // Generate a playlist based on the given mood
  const generatePlaylist = useCallback(async (
    mood: string,
    geminiApiKey: string,
    options: PlaylistOptions
  ) => {
    setIsLoading(true)
    setError(null)

    try {
      // First, create a new playlist
      const playlistResponse = await spotifyFetch(`${SPOTIFY_API_URL}/me/playlists`, {
        method: 'POST',
        body: JSON.stringify({
          name: `mood - ${mood}`,
          description: `A playlist for when you're feeling ${mood.toLowerCase()}. Generated on ${new Date().toLocaleDateString()}`,
          public: true // Make it public so it's easily accessible
        }),
      })

      // Then get song recommendations from Gemini
      const geminiResponse = await fetch('/api/generate-recommendations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mood,
          apiKey: geminiApiKey,
          options,
        }),
      })

      if (!geminiResponse.ok) {
        throw new Error('Failed to generate recommendations')
      }

      const recommendations = await geminiResponse.json()

      // Search for and add the recommended tracks
      const trackUris = await Promise.all(
        recommendations.tracks.map(async (track: string) => {
          const searchResponse = await spotifyFetch(
            `${SPOTIFY_API_URL}/search?q=${encodeURIComponent(track)}&type=track&limit=1`
          )
          return searchResponse.tracks.items[0]?.uri
        })
      )

      const validTrackUris = trackUris.filter(Boolean)

      // Add tracks to the playlist in batches of 50 (Spotify's limit)
      if (validTrackUris.length > 0) {
        for (let i = 0; i < validTrackUris.length; i += 50) {
          const batch = validTrackUris.slice(i, i + 50)
          await spotifyFetch(`${SPOTIFY_API_URL}/playlists/${playlistResponse.id}/tracks`, {
            method: 'POST',
            body: JSON.stringify({
              uris: batch,
            }),
          })
        }
      }

      return playlistResponse.id
    } catch (err) {
      console.error('Error generating playlist:', err)
      setError(err instanceof Error ? err.message : 'Unknown error')
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [spotifyFetch])
  
  const getUserPlaylists = async (): Promise<SpotifyPlaylist[]> => {
    const response = await spotifyFetch(`${SPOTIFY_API_URL}/me/playlists?limit=50`)
    // Only return user-created mood playlists (they start with "mood -")
    return response.items.filter((playlist: SpotifyPlaylist) => 
      playlist.name.toLowerCase().startsWith('mood -')
    )
  }
  
  const deletePlaylist = async (playlistId: string) => {
    try {
      console.log('Attempting to delete playlist:', playlistId)
      
      // First, verify the playlist exists and get its details
      const playlist = await spotifyFetch(`https://api.spotify.com/v1/playlists/${playlistId}`)
      const userId = localStorage.getItem('spotify_user_id')
      
      console.log('Playlist owner:', playlist.owner.id, 'Current user:', userId)
      
      if (playlist.owner.id !== userId) {
        throw new Error("You can only delete playlists you've created")
      }
      
      // Use appropriate DELETE endpoint
      const response = await fetch(`https://api.spotify.com/v1/playlists/${playlistId}/followers`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${tokens?.accessToken}`
        }
      })
      
      if (!response.ok) {
        // Try to get error details if possible
        let errorMessage = `Failed to delete playlist (HTTP ${response.status})`
        try {
          const errorData = await response.json()
          errorMessage = errorData.error?.message || errorMessage
        } catch (e) {
          // Ignore if we can't parse the error
        }
        
        console.error('Delete playlist response error:', response.status, errorMessage)
        throw new Error(errorMessage)
      }
      
      console.log('Playlist deleted successfully')
      return true
    } catch (err) {
      console.error('Error in deletePlaylist:', err)
      throw err
    }
  }
  
  const playPlaylist = async (playlistId: string, deviceId?: string) => {
    if (deviceId) {
      await spotifyFetch(
        `https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`,
        {
          method: 'PUT',
          body: JSON.stringify({
            context_uri: `spotify:playlist:${playlistId}`
          })
        }
      )
    }
  }

  // Root URL callback handler - used when authentication occurs in the same tab
  const handleAuthCallback = useCallback(async (code: string, state: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Validate state
      const storedState = localStorage.getItem('spotify_auth_state');
      if (!storedState || storedState !== state) {
        throw new Error('State validation failed. Possible CSRF attack.');
      }
      
      // Get code verifier from localStorage
      const codeVerifier = localStorage.getItem('code_verifier');
      if (!codeVerifier) {
        throw new Error('PKCE code verifier not found');
      }
      
      // Exchange code for tokens
      const response = await fetch('/api/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          grant_type: 'authorization_code',
          code,
          redirect_uri: window.location.origin, // Use root URL as redirect
          code_verifier: codeVerifier,
          client_id: clientId
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error_description || errorData.error || 'Failed to exchange code for tokens');
      }
      
      const data: SpotifyTokenResponse = await response.json();
      
      // Store tokens
      const newTokens: SpotifyTokens = {
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresAt: Date.now() + data.expires_in * 1000
      };
      
      // Save tokens
      handleTokens(newTokens);
      
      // Clean up authentication data
      localStorage.removeItem('code_verifier');
      localStorage.removeItem('spotify_auth_state');
      
      // Fetch user ID for later use
      try {
        const userResponse = await fetch('https://api.spotify.com/v1/me', {
          headers: {
            'Authorization': `Bearer ${newTokens.accessToken}`
          }
        });
        
        if (userResponse.ok) {
          const userData = await userResponse.json();
          localStorage.setItem('spotify_user_id', userData.id);
        }
      } catch (e) {
        console.error('Failed to fetch user data:', e);
        // Non-critical error, don't throw
      }
      
      return data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to handle callback';
      console.error('Callback error:', errorMessage);
      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [clientId, handleTokens]);

  return {
    isAuthenticated,
    connectToSpotify,
    handleSpotifyCallback,
    handleAuthCallback,
    generatePlaylist,
    getUserPlaylists,
    deletePlaylist,
    playPlaylist,
    spotifyFetch,
    isLoading,
    error,
    clientId,
    logout
  }
} 